import React, { useEffect, useState, useRef } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { assetIcons } from "../../../constants/assets";
import { fontSizes } from "../../../theme/font-sizes";
import { darkColors } from "../../../theme/colors";
import apiRoutes from "../../../api/routes";
import useApiRequest from "../../../api/api";
import InlineSvg from "../inline-svg";

export const ChatSection = () => {
  const { makeApiCall } = useApiRequest();
  const [isLoading, setIsLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [chatMessages, setChatMessages] = useState([
    { type: "assistant", text: "Hi, I'm here to assist you with everything" },
  ]);
  const chatContainerRef = useRef(null);

  const fetchQuestions = async () => {
    setIsLoading(true);
    try {
      const response = await makeApiCall(apiRoutes.support.list, "GET");
      if (response?.data) {
        setQuestions(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch questions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubQuestions = async (id) => {
    setIsLoading(true);
    try {
      const response = await makeApiCall(apiRoutes.support.sub_list(id), "GET");
      if (response?.data) {
        setQuestions(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch sub-questions:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, questions]);
  const handleQuestionClick = async (question) => {
    // Add user's question to chat
    setChatMessages((prev) => [
      ...prev,
      ...questions.map((question) => ({
        type: "assistant",
        text: `${question.question}`, // Display the question from the assistant
      })),
      {
        type: "user",
        text: `${question.question}`,
      },
    ]);

    if (question.answer === null) {
      // If no answer, fetch sub-questions
      await fetchSubQuestions(question.identifier);
    } else {
      // If we have an answer, show it and the follow-up message
      setChatMessages((prev) => [
        ...prev,
        { type: "assistant", text: question.answer, isAnswer: true },
        {
          type: "bot",
          text: "Click here if you have any more questions.",
        },
      ]);
      setQuestions([]); // Clear questions when answer is found
    }
  };

  const handleMoreQuestionsClick = () => {
    // Clear chat except for initial message and reload questions
    setChatMessages([
      {
        type: "assistant",
        text: "Hi, I'm here to assist you with everything",
      },
    ]);
    fetchQuestions();
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  return (
    <Box
      sx={{
        bgcolor: "#1a1a1a",
        borderRadius: "12px",
        height: "380px", // Fixed height
        maxWidth: { lg: "500px", xs: "100%" },
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#262525",
          width: "100%",
          borderTopRightRadius: "12px",
          borderTopLeftRadius: "12px",
          p: { lg: 1.5 },
          pl: { lg: 2.5 },
          pr: { lg: 2.5 },
        }}
      >
        <Box display={"flex"} sx={{ p: { lg: 0, xs: 2 } }}>
          {/* <Box component={"img"} src={assetIcons.headphones} sx={{ mr: 1.5 }} /> */}
          <InlineSvg url={assetIcons.headphones} sx={{ mr: 1.5, mt: 1.5 }} />
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                color: "#fff",
                fontWeight: 500,
                fontSize: "1rem",
                textAlign: "left",
              }}
            >
              Jeniffer
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "0.875rem",
              }}
            >
              Ready To Assist
            </Typography>
          </Box>
        </Box>

        <InlineSvg url={assetIcons.chatOnline} sx={{ p: { lg: 0, xs: 2 } }} />
      </Box>

      {/* Connected message */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          my: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            height: "1px",
            backgroundColor: darkColors.gray,
            mx: 0.5,
            ml: 2,
          }}
        />
        <Typography
          align="center"
          fontSize={fontSizes.body}
          sx={{
            color: darkColors.maroon,
          }}
        >
          You are now connected with Jeniffer
        </Typography>
        <Box
          sx={{
            flex: 1,
            height: "1px",
            backgroundColor: darkColors.gray,
            mx: 0.5,
            mr: 2,
          }}
        />
      </Box>

      {/* Scrollable chat container */}
      <Box
        ref={chatContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2.5,
          py: 0,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#1a1a1a",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        {/* Chat messages */}
        {chatMessages.map((message, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent:
                message.type === "user" ? "flex-end" : "flex-start",
              mb: 2,
              position: "relative", // Position to place "You" outside
            }}
          >
            <Box
              sx={{
                bgcolor: "#222222",
                borderRadius: "12px",
                p: 2,
                maxWidth: "80%",
                ...(message.type === "user" && {
                  bgcolor: darkColors.buttonBottom,
                }),
                ...(message.type === "bot" && {
                  "&:hover": {
                    bgcolor: "#2a2a2a",
                  },
                }),
                opacity:
                  message.type === "assistant" && !message.isAnswer ? 0.5 : 1, // Apply opacity to assistant except for the first one
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "0.875rem",
                  cursor: message.type === "bot" ? "pointer" : "default",
                  textAlign: "left",
                  whiteSpace: "pre-line"
                }}
                onClick={
                  message.type === "bot" ? handleMoreQuestionsClick : undefined
                }
              >
                {message.text}
              </Typography>
            </Box>

            {/* Display "You" if message is from the user */}
            {message.type === "user" && (
              <Typography
                sx={{
                  position: "absolute",
                  bottom: "-18px", // Adjust the position as needed
                  right: "10px", // Align to the right
                  color: "#fff",
                  fontSize: "0.75rem",
                }}
              >
                You
              </Typography>
            )}
          </Box>
        ))}

        {/* Questions list */}
        {isLoading ? (
          <Box
            sx={{
              bgcolor: "#222222",
              borderRadius: "12px",
              p: 2,
              mb: 2,
              cursor: "pointer",
              "&:hover": {
                bgcolor: "#2a2a2a",
              },
              maxWidth: 60,
            }}
          >
            <Typography color="white" fontWeight={700}>
              . . .
            </Typography>
          </Box>
        ) : (
          questions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {questions.map((question) => (
                <Box
                  key={question.identifier}
                  onClick={() => handleQuestionClick(question)}
                  sx={{
                    bgcolor: "#222222",
                    borderRadius: "12px",
                    p: 2,
                    mb: 2,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "#2a2a2a",
                    },
                    maxWidth: "80%",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: "0.875rem",
                    }}
                    textAlign={"left"}
                    whiteSpace={"pre-line"}
                  >
                    {question.question}
                  </Typography>
                </Box>
              ))}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default ChatSection;
